"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContentI18nPolicyError = void 0;
exports.applyContentI18nToSources = applyContentI18nToSources;
const content_i18n_1 = require("./content-i18n");
const TRANSLATIONS_SOURCE_KEY = 'translations';
const I18N_CELL_KEY = '__i18n';
class ContentI18nPolicyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ContentI18nPolicyError';
    }
}
exports.ContentI18nPolicyError = ContentI18nPolicyError;
function applyContentI18nToSources(input) {
    if (input.locale === input.defaultLocale)
        return input.sources;
    const translations = input.sources[TRANSLATIONS_SOURCE_KEY];
    const entries = translations
        ? (0, content_i18n_1.parseContentTranslationRows)(translations.rows, translations.columns).filter(entry => entry.locale === input.locale && (0, content_i18n_1.isPublishedContentTranslation)(entry))
        : [];
    const entriesByEntity = new Map();
    for (const entry of entries) {
        const current = entriesByEntity.get(entry.entityDb) ?? [];
        current.push(entry);
        entriesByEntity.set(entry.entityDb, current);
    }
    return Object.fromEntries(Object.entries(input.sources).map(([sourceKey, source]) => {
        if (sourceKey === TRANSLATIONS_SOURCE_KEY)
            return [sourceKey, source];
        const sourceEntries = [
            ...(entriesByEntity.get(sourceKey) ?? []),
            ...(entriesByEntity.get(source.source.blockId) ?? []),
        ];
        validateCoverage({
            source,
            entries: sourceEntries,
            locale: input.locale,
            policy: input.policy ?? content_i18n_1.DEFAULT_CONTENT_I18N_POLICY,
        });
        if (sourceEntries.length === 0 && source.source.role !== 'content') {
            return [sourceKey, source];
        }
        return [
            sourceKey,
            {
                ...source,
                rows: applyEntriesToRows(source.rows, source.columns, sourceEntries, input.locale, input.defaultLocale, source.source.contentModel),
            },
        ];
    }));
}
function validateCoverage(input) {
    if (input.policy.missingTranslation !== 'strict')
        return;
    const translated = new Set(input.entries
        .filter(entry => entry.locale === input.locale)
        .filter(content_i18n_1.isPublishedContentTranslation)
        .filter(entry => hasContent(entry.value))
        .map(entry => {
        const fieldKey = resolveContentFieldKey(entry.field, input.source.columns);
        return fieldKey
            ? `${entry.entityRowId}\u0000${fieldKey}`
            : `invalid\u0000${entry.field}`;
    }));
    for (const row of input.source.rows) {
        for (const column of input.source.columns) {
            if (!(0, content_i18n_1.isContentI18nTranslatableSourceColumn)({
                column,
                columns: input.source.columns,
                contentModel: input.source.source.contentModel,
            }) ||
                !hasContent(row.cells[column.id])) {
                continue;
            }
            if (!translated.has(`${row.id}\u0000${column.id}`)) {
                throw new ContentI18nPolicyError(`Missing ${input.locale} translation for row "${row.id}" field "${column.name}"`);
            }
        }
    }
}
function applyEntriesToRows(rows, columns, entries, locale, defaultLocale, contentModel) {
    const byRow = new Map();
    for (const entry of entries) {
        const current = byRow.get(entry.entityRowId) ?? [];
        current.push(entry);
        byRow.set(entry.entityRowId, current);
    }
    return rows.map(row => {
        const rowEntries = byRow.get(row.id) ?? [];
        const requiredFields = columns
            .filter(column => (0, content_i18n_1.isContentI18nTranslatableSourceColumn)({
            column,
            columns,
            contentModel,
        }))
            .filter(column => hasContent(row.cells[column.id]))
            .map(column => column.id);
        const translatedFields = new Set(rowEntries
            .filter(entry => hasContent(entry.value))
            .map(entry => resolveContentFieldKey(entry.field, columns))
            .filter((field) => Boolean(field)));
        const missingFields = requiredFields.filter(field => !translatedFields.has(field));
        if (missingFields.length > 0) {
            return {
                ...row,
                cells: {
                    ...row.cells,
                    [content_i18n_1.CONTENT_I18N_ROW_STATE_KEY]: {
                        state: 'fallback',
                        requestedLocale: locale,
                        resolvedLocale: defaultLocale,
                        defaultLocale,
                        missingFields,
                    },
                },
            };
        }
        const cells = { ...row.cells };
        const i18n = { ...readI18nCell(cells) };
        let applied = 0;
        for (const entry of rowEntries) {
            if (!hasContent(entry.value))
                continue;
            const fieldKey = resolveContentFieldKey(entry.field, columns);
            if (!fieldKey)
                continue;
            const column = columns.find(candidate => candidate.id === fieldKey);
            if (!column ||
                !(0, content_i18n_1.isContentI18nTranslatableSourceColumn)({
                    column,
                    columns,
                    contentModel,
                })) {
                continue;
            }
            cells[fieldKey] = entry.value;
            i18n[fieldKey] = { ...i18n[fieldKey], [locale]: entry.value };
            applied += 1;
        }
        if (applied === 0)
            return row;
        cells[I18N_CELL_KEY] = i18n;
        cells[content_i18n_1.CONTENT_I18N_ROW_STATE_KEY] = {
            state: 'translated',
            requestedLocale: locale,
            resolvedLocale: locale,
            defaultLocale,
            missingFields: [],
        };
        return { ...row, cells };
    });
}
function resolveContentFieldKey(field, columns) {
    return (columns.find(column => column.id === field)?.id ??
        columns.find(column => column.name === field)?.id ??
        null);
}
function hasContent(value) {
    return value !== null && value !== undefined && value !== '';
}
function readI18nCell(cells) {
    const raw = cells[I18N_CELL_KEY];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return {};
    return raw;
}
